import format from "date-fns/format";
import parse from "date-fns/parse";
import type { ReactElement } from "react";
import type { Omit } from "utility-types";
import type { IYearlyBrazilianCompanyFinancialStatementRow } from "../../../../../types/graphqlTypes";
import FinancialValueThresholdColumnStyles from "../../../../financials/FinancialValueThresholdColumnStyles";
import * as palette from "../../../../palette";
import TextTooltip from "../../../../shared/TextTooltip";
import formatFinancialValue from "../../formatFinancialValue";

interface IYearlyCompanyFinancialsProps {
  displayAllColumns: boolean;
  financials: Omit<IYearlyBrazilianCompanyFinancialStatementRow, "__typename">[];
}

export default function YearlyCompanyFinancials({
  displayAllColumns,
  financials,
}: IYearlyCompanyFinancialsProps): ReactElement | null {
  if (financials.length === 0) {
    return null;
  }

  const hasNetIncomeFromDiscontinuedOperations = financials.some((financialStatementRow) => {
    return financialStatementRow.netIncomeFromDiscontinuedOperations !== 0;
  });

  return (
    <table
      css={{
        borderRadius: 4,
        borderSpacing: 0,
        borderCollapse: "collapse",
        "> thead > tr > th:first-of-type": {
          borderTopLeftRadius: 4,
        },
        "> thead > tr > th:last-of-type": {
          borderTopRightRadius: 4,
        },
        "> tbody > tr:last-of-type > td:first-of-type": {
          borderBottomLeftRadius: 4,
        },
        "> tbody > tr:last-of-type > td:last-of-type": {
          borderBottomRightRadius: 4,
        },
        "> thead > tr > th": {
          position: "sticky",
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
        "& > tbody > tr > td": {
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
      }}
    >
      <thead>
        <tr>
          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.white,
              fontWeight: 600,
              backgroundColor: palette.blueBayoux,
              borderRight: `solid 1px ${palette.lynch}`,
            }}
          >
            Ano
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.lynch}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netEquity" placement="top" content="Ativos - Passivos">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Pat. Líq.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netRevenue" placement="top" content="Vendas de Produtos ou Serviços">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Receita Líq.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/ebitda"
              placement="top"
              content="Earnings Before Interest, Taxes, Depreciation and Amortization"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  EBITDA
                </span>
              )}
            </TextTooltip>
          </th>

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/depreciationAndAmortization"
                placement="top"
                content="Depreciação e Amortização"
                style={{
                  maxWidth: 260,
                  textAlign: "center",
                }}
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    D&A
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/ebit"
                placement="top"
                content="Earnings Before Interest and Taxes"
                style={{
                  maxWidth: 260,
                  textAlign: "center",
                }}
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    EBIT
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/financialResult"
              placement="top"
              content="Receitas Financeiras - Despesas Financeiras"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Res. Fin.
                </span>
              )}
            </TextTooltip>
          </th>

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/incomeTaxes"
                placement="top"
                content="Impostos"
                style={{
                  maxWidth: 260,
                  textAlign: "center",
                }}
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    Impostos
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          {displayAllColumns && hasNetIncomeFromDiscontinuedOperations && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/netIncomeFromDiscontinuedOperations"
                placement="top"
                content="Resultado Líquido das Operações Descontinuadas"
                style={{
                  maxWidth: 260,
                  textAlign: "center",
                }}
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    Op. Desc.
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/netIncome"
              placement="top"
              content="Receita Líquida - Descontos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Lucro Líq.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/netMargin"
              placement="top"
              content="Lucro Líquido / Receita Líquida"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Mrg. Líq.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.seaGreen,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/returnOnEquity"
              placement="top"
              content="Return On Equity (Lucro Líquido / Patrimônio Líquido)"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  ROE
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #eec9c9`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/cash"
              placement="top"
              content="Capital Com Liquidez"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Caixa
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/debt"
              placement="top"
              content="Empréstimos e Financiamentos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Dívida
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/netDebtToEbitdaRatio"
              placement="top"
              content="Bom < 3"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  D. L. / EBITDA
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/cashFlowFromOperations"
              placement="top"
              content="Fluxo de Caixa Operacional"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  FCO
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/capex"
              placement="top"
              content="Capital Expenditure"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  CAPEX
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/cashFlowFromFinancing"
              placement="top"
              content="Fluxo de Caixa de Financiamentos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  FCF
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/freeCashFlow"
              placement="top"
              content="FCO - Capex"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  FCL CAPEX
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.luxorGold,
              fontWeight: 600,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/payout"
              placement="top"
              content="Dividendos e JCP Distribuídos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Prov.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.luxorGold,
              fontWeight: 600,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/payoutToNetIncomeRatio"
              placement="top"
              content="Proventos / Lucro Líquido"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Payout
                </span>
              )}
            </TextTooltip>
          </th>
        </tr>
      </thead>

      <tbody
        css={{
          "& > tr > td": {
            textAlign: "center",
          },
          "& > tr > td:not(:first-of-type)": {
            borderLeft: `solid 1px ${palette.mystic}`,
            borderRight: `solid 1px ${palette.mystic}`,
            borderBottom: `solid 1px ${palette.mystic}`,
          },
          "& > tr:hover": {
            backgroundColor: "rgb(241, 244, 248, 0.5)",
          },
          "& > tr:last-of-type > td": {
            borderBottom: "none",
          },
        }}
      >
        {financials.map((financialStatementRow) => {
          return (
            <tr key={financialStatementRow.fiscalYear}>
              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  backgroundColor: palette.blueBayoux,
                  fontWeight: 600,
                  color: palette.white,
                  borderTop: `solid 1px ${palette.lynch}`,
                  borderBottom: `solid 1px ${palette.lynch}`,
                  borderRight: `solid 1px ${palette.lynch}`,
                }}
              >
                {financialStatementRow.isTrailingTwelveMonths ? (
                  <TextTooltip
                    id={`tooltips/th/trailingTwelveMonths-${financialStatementRow.endOfPeriod}`}
                    placement="top"
                    content="Acumulado dos últimos 12 meses"
                    style={{
                      maxWidth: 260,
                      textAlign: "center",
                    }}
                  >
                    {({ accessibility, bind, ref }) => (
                      <span {...accessibility} {...bind} ref={ref}>
                        {format(parse(financialStatementRow.endOfPeriod, "yyyy-MM-dd", new Date()), "MM/yy 'TTM'")}
                      </span>
                    )}
                  </TextTooltip>
                ) : (
                  <span>{format(parse(financialStatementRow.endOfPeriod, "yyyy-MM-dd", new Date()), "MM/yyyy")}</span>
                )}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.netEquity < 0 ? palette.mojo : palette.punga,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.netEquity)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.fruitSalad,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.netRevenue)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: financialStatementRow.ebitda < 0 ? palette.mojo : palette.seaGreen,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.ebitda)}
              </td>

              {displayAllColumns && (
                <FinancialValueThresholdColumnStyles
                  value={financialStatementRow.depreciationAndAmortization}
                  comparators={[(value) => value >= 0, (value) => value < 0]}
                  styles={[
                    () => ({
                      color: palette.seaGreen,
                    }),
                    () => ({
                      color: palette.mojo,
                    }),
                  ]}
                >
                  {(depreciationAndAmortization, css) => {
                    return (
                      <td
                        css={{
                          fontFamily: "novel-mono-pro-condensed",
                          fontWeight: 500,
                          borderTop: `solid 1px ${palette.fringyFlower}`,
                          borderLeft: `solid 1px ${palette.fringyFlower}`,
                          borderBottom: `solid 1px ${palette.fringyFlower}`,
                          ...css,
                        }}
                      >
                        {formatFinancialValue(depreciationAndAmortization)}
                      </td>
                    );
                  }}
                </FinancialValueThresholdColumnStyles>
              )}

              {displayAllColumns && (
                <FinancialValueThresholdColumnStyles
                  value={financialStatementRow.ebit}
                  comparators={[(value) => value >= 0, (value) => value < 0]}
                  styles={[
                    () => ({
                      color: palette.seaGreen,
                    }),
                    () => ({
                      color: palette.mojo,
                    }),
                  ]}
                >
                  {(ebit, css) => {
                    return (
                      <td
                        css={{
                          fontFamily: "novel-mono-pro-condensed",
                          fontWeight: 500,
                          borderTop: `solid 1px ${palette.fringyFlower}`,
                          borderLeft: `solid 1px ${palette.fringyFlower}`,
                          borderBottom: `solid 1px ${palette.fringyFlower}`,
                          ...css,
                        }}
                      >
                        {formatFinancialValue(ebit)}
                      </td>
                    );
                  }}
                </FinancialValueThresholdColumnStyles>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: financialStatementRow.financialResult >= 0 ? palette.seaGreen : palette.mojo,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.financialResult)}
              </td>

              {displayAllColumns && (
                <FinancialValueThresholdColumnStyles
                  value={financialStatementRow.incomeTaxes}
                  comparators={[(value) => value >= 0, (value) => value < 0]}
                  styles={[
                    () => ({
                      color: palette.seaGreen,
                    }),
                    () => ({
                      color: palette.mojo,
                    }),
                  ]}
                >
                  {(incomeTaxes, css) => {
                    return (
                      <td
                        css={{
                          fontFamily: "novel-mono-pro-condensed",
                          fontWeight: 500,
                          borderTop: `solid 1px ${palette.fringyFlower}`,
                          borderLeft: `solid 1px ${palette.fringyFlower}`,
                          borderBottom: `solid 1px ${palette.fringyFlower}`,
                          ...css,
                        }}
                      >
                        {formatFinancialValue(incomeTaxes)}
                      </td>
                    );
                  }}
                </FinancialValueThresholdColumnStyles>
              )}

              {displayAllColumns && hasNetIncomeFromDiscontinuedOperations && (
                <FinancialValueThresholdColumnStyles
                  value={financialStatementRow.netIncomeFromDiscontinuedOperations}
                  comparators={[(value) => value >= 0, (value) => value < 0]}
                  styles={[
                    () => ({
                      color: palette.seaGreen,
                    }),
                    () => ({
                      color: palette.mojo,
                    }),
                  ]}
                >
                  {(netIncomeFromDiscontinuedOperations, css) => {
                    return (
                      <td
                        css={{
                          fontFamily: "novel-mono-pro-condensed",
                          fontWeight: 500,
                          borderTop: `solid 1px ${palette.fringyFlower}`,
                          borderLeft: `solid 1px ${palette.fringyFlower}`,
                          borderBottom: `solid 1px ${palette.fringyFlower}`,
                          ...css,
                        }}
                      >
                        {formatFinancialValue(netIncomeFromDiscontinuedOperations)}
                      </td>
                    );
                  }}
                </FinancialValueThresholdColumnStyles>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.netIncome >= 0 ? palette.oceanGreen : palette.mojo,
                  border: `solid 1px ${palette.fringyFlower}`,
                  borderRight: "none",
                }}
              >
                {formatFinancialValue(financialStatementRow.netIncome)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  color:
                    financialStatementRow.netMargin < 0 || financialStatementRow.netIncome < 0
                      ? palette.mojo
                      : palette.blueBayoux,
                  fontWeight: financialStatementRow.netMargin < 0 || financialStatementRow.netIncome < 0 ? 600 : 500,
                  borderLeft: `solid 1px ${palette.mystic}`,
                  border: `solid 1px ${palette.mystic}`,
                  backgroundColor: "#fafafa",
                }}
              >
                {financialStatementRow.netMargin < 0 || financialStatementRow.netIncome < 0
                  ? "P"
                  : `${financialStatementRow.netMargin
                      .toFixed(financialStatementRow.netMargin < 1 && financialStatementRow.netMargin !== 0 ? 1 : 0)
                      .replace(".", ",")}%`}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight:
                    (financialStatementRow.returnOnEquity || 0) < 0 ||
                    financialStatementRow.netIncome < 0 ||
                    financialStatementRow.netEquity < 0
                      ? 600
                      : 500,
                  color:
                    (financialStatementRow.returnOnEquity || 0) < 0 ||
                    financialStatementRow.netIncome < 0 ||
                    financialStatementRow.netEquity < 0
                      ? palette.mojo
                      : palette.blueBayoux,
                  lineHeight: "20px",
                  borderLeft: `solid 1px ${palette.mystic}`,
                  border: `solid 1px ${palette.mystic}`,
                  borderRight: "none",
                  backgroundColor: "#fafafa",
                }}
              >
                {(financialStatementRow.returnOnEquity || 0) < 0 ||
                financialStatementRow.netIncome < 0 ||
                financialStatementRow.netEquity < 0
                  ? "P"
                  : `${(financialStatementRow.returnOnEquity || 0)
                      .toFixed((financialStatementRow.returnOnEquity || 0) < 1 ? 1 : 0)
                      .replace(".", ",")}%`}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.dixie,
                  border: `solid 1px #eec9c9`,
                }}
              >
                {formatFinancialValue(financialStatementRow.cash)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.mojo,
                  border: `solid 1px #eec9c9`,
                }}
              >
                {formatFinancialValue(financialStatementRow.debt)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.netDebtToEbitdaRatio}
                comparators={[
                  (value) => {
                    return value >= 0 && value < 2;
                  },
                  (value) => {
                    return value >= 2 && value < 3;
                  },
                  (value) => {
                    return value >= 3;
                  },
                ]}
                styles={[
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                ]}
              >
                {(netDebtToEbitdaRatio, css) => {
                  if (financialStatementRow.ebitda < 0) {
                    return (
                      <td
                        css={{
                          fontFamily: "novel-mono-pro-condensed",
                          fontWeight: 700,
                          border: `solid 1px #eec9c9`,
                          color: palette.mojo,
                          backgroundColor: "#FCF1F1",
                        }}
                      >
                        P
                      </td>
                    );
                  }

                  return (
                    <td
                      css={{
                        fontFamily: "novel-mono-pro-condensed",
                        fontWeight: 500,
                        color: palette.mojo,
                        border: `solid 1px #eec9c9`,
                        ...css,
                      }}
                    >
                      {netDebtToEbitdaRatio !== null && netDebtToEbitdaRatio % 1 === 0
                        ? `${netDebtToEbitdaRatio},0`
                        : String(netDebtToEbitdaRatio).replace(".", ",")}
                    </td>
                  );
                }}
              </FinancialValueThresholdColumnStyles>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.cashFlowFromOperations}
                comparators={[(value) => value > 0, (value) => value < 0]}
                styles={[
                  () => ({
                    color: palette.tundora,
                  }),
                  () => ({
                    color: palette.stiletto,
                  }),
                ]}
              >
                {(cashFlowFromOperations, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 600,
                      border: `solid 1px ${palette.frenchPass}`,
                      ...css,
                    }}
                  >
                    {formatFinancialValue(cashFlowFromOperations)}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: palette.tundora,
                  border: `solid 1px ${palette.frenchPass}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.capex)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.cashFlowFromFinancing}
                comparators={[(value) => value > 0, (value) => value < 0]}
                styles={[
                  () => ({
                    color: palette.stiletto,
                  }),
                  () => ({
                    color: palette.tundora,
                  }),
                ]}
              >
                {(cashFlowFromFinancing, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 500,
                      border: `solid 1px ${palette.frenchPass}`,
                      ...css,
                    }}
                  >
                    {formatFinancialValue(cashFlowFromFinancing)}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.freeCashFlow}
                comparators={[(value) => value > 0, (value) => value < 0]}
                styles={[
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                  () => ({
                    color: palette.stiletto,
                    backgroundColor: "#FCF1F1",
                  }),
                ]}
              >
                {(freeCashFlow, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 600,
                      border: `solid 1px ${palette.frenchPass}`,
                      borderRight: "none",
                      ...css,
                    }}
                  >
                    {formatFinancialValue(freeCashFlow)}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              <td
                css={{
                  color: palette.luxorGold,
                  fontWeight: 500,
                  fontSize: 14,
                  fontFamily: "novel-mono-pro-condensed",
                  lineHeight: "20px",
                  border: `solid 1px ${palette.chamois}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.cashFlowForDividends || financialStatementRow.payout)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={
                  financialStatementRow.cashFlowForDividendsToNetIncomeRatio ||
                  financialStatementRow.payoutToNetIncomeRatio
                }
                comparators={[
                  () => {
                    return financialStatementRow.netIncome < 0;
                  },
                  (value) => {
                    return value > 0 && value < 30;
                  },
                  (value) => {
                    return value >= 30 && value < 70;
                  },
                  (value) => {
                    return value >= 70;
                  },
                ]}
                styles={[
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                ]}
              >
                {(payoutOrCashFlowForDividendsToNetIncomeRatio, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 500,
                      color: palette.luxorGold,
                      lineHeight: "20px",
                      fontSize: 14,
                      border: `solid 1px ${palette.chamois}`,
                      borderRight: "none",
                      ...css,
                    }}
                  >
                    {financialStatementRow.netIncome < 0 ? "P" : `${payoutOrCashFlowForDividendsToNetIncomeRatio}%`}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
